import { useState, useEffect, useRef, Component, type ReactNode, type ErrorInfo } from "react";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient, getQueryFn, apiRequest } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { ThemeProvider } from "@/lib/theme";
import Capture from "@/pages/Capture";
import Timeline from "@/pages/Timeline";
import Patterns from "@/pages/Patterns";
import Trends from "@/pages/Trends";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Chat from "@/pages/Chat";
import Home from "@/pages/Home";
import DailyRecaps from "@/pages/DailyRecaps";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ProfileBuilder from "@/pages/ProfileBuilder";
import Tutorial from "@/pages/Tutorial";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/not-found";
import TestIconCard from "@/pages/TestIconCard";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    try {
      fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error.message,
          stack: error.stack?.slice(0, 500),
          context: `ErrorBoundary componentDidCatch: ${errorInfo.componentStack?.slice(0, 200)}`,
        }),
      }).catch(() => {});
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      const errMsg = this.state.error?.message || "Unknown error";
      return (
        <div
          data-testid="error-boundary-fallback"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            padding: "32px",
            textAlign: "center",
             background: "var(--page-bg)",
             color: "var(--text-primary)",
            zIndex: 99998,
          }}
        >
           <div className="type-reflective" style={{ fontSize: "1.125rem", color: "var(--text-primary)" }}>
            Something went wrong
          </div>
           <p className="type-reflective" style={{ fontSize: "0.9375rem", color: "var(--text-secondary)", maxWidth: "320px", lineHeight: "1.6" }}>
            {this.props.fallbackMessage || "This page ran into an issue. Try refreshing."}
          </p>
           <p className="type-label" style={{ color: "var(--text-muted)", maxWidth: "320px", wordBreak: "break-all" }}>
            {errMsg.slice(0, 200)}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              marginTop: "8px",
              padding: "10px 24px",
               color: "var(--primary-foreground)",
               background: "hsl(var(--primary))",
              border: "none",
              borderRadius: "8px",
               fontFamily: "var(--font-display)",
              cursor: "pointer",
            }}
            data-testid="button-error-reload"
          >
            RELOAD
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const sidebarStyle = {
  "--sidebar-width": "17rem",
  "--sidebar-width-icon": "3rem",
};

function PageTransition({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [key, setKey] = useState(location);

  useEffect(() => {
    setKey(location);
  }, [location]);

  return (
    <div
      key={key}
      className="page-enter flex-1 flex flex-col overflow-y-auto"
      style={location === "/" ? { animation: "none" } : undefined}
    >
      {children}
    </div>
  );
}

function MainApp({ userTimeZone }: { userTimeZone: string | null }) {
  const [showTutorial, setShowTutorial] = useState(false);
  const [location] = useLocation();
  // Only Home earns the date line; other screens have their own titles.
  const showDateHeader = location === "/";

  useEffect(() => {
    const handler = () => setShowTutorial(true);
    window.addEventListener("mirror-retake-tutorial", handler);
    return () => window.removeEventListener("mirror-retake-tutorial", handler);
  }, []);

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties} defaultOpen={true}>
      <div className="flex h-screen w-full overflow-hidden" style={{ background: "var(--page-bg)" }}>
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <header
            className="flex items-center gap-3 px-5 py-3 shrink-0"
            style={{ borderBottom: "1px solid var(--border-hairline)" }}
          >
            <SidebarTrigger
              data-testid="button-sidebar-toggle"
              className="transition-colors"
              style={{ color: "var(--text-muted)" }}
            />
            {showDateHeader && (
              <div
                 className="type-label"
                 style={{ color: "var(--text-muted)" }}
              >
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
            )}
          </header>
          <main className="flex-1 overflow-hidden flex flex-col">
            <ErrorBoundary>
              <PageTransition>
                <Switch>
                  <Route path="/" component={Home} />
                  <Route path="/chat" component={Chat} />
                  <Route path="/log" component={Capture} />
                  <Route path="/entries/:entryId">
                    {(params) => <Timeline entryId={params.entryId} timeZone={userTimeZone} />}
                  </Route>
                  <Route path="/entries">
                    <Timeline timeZone={userTimeZone} />
                  </Route>
                  <Route path="/patterns" component={Patterns} />
                  <Route path="/recaps" component={DailyRecaps} />
                  <Route path="/trends" component={Trends} />
                  <Route path="/profile"><Redirect to="/patterns" /></Route>
                  <Route path="/settings" component={Settings} />
                  <Route path="/admin" component={Admin} />
                  <Route component={NotFound} />
                </Switch>
              </PageTransition>
            </ErrorBoundary>
          </main>
        </div>
      </div>
      {showTutorial && (
        <Tutorial onComplete={() => setShowTutorial(false)} />
      )}
    </SidebarProvider>
  );
}

function AppShell() {
  const [location] = useLocation();

  const { data: user, isLoading } = useQuery<any>({
    queryKey: ["/api/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    staleTime: 30000,
  });

  const tzSynced = useRef(false);
  useEffect(() => {
    if (user?.id && !tzSynced.current) {
      tzSynced.current = true;
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && browserTz !== user.timezone) {
        apiRequest("POST", "/api/auth/timezone", { timezone: browserTz }).catch(() => {});
      }
    }
  }, [user]);

  // Dev-only fixture page — bypass all auth guards so Playwright can reach it.
  if (import.meta.env.DEV && location === "/test-mirror-card") {
    return <TestIconCard />;
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--page-bg)" }}
      >
        <div className="flex flex-col items-center gap-4">
          <div
             className="text-xl tracking-[0.1em] type-display"
             style={{ color: "var(--fg-raw)" }}
          >
            FRACTAL
          </div>
          <div className="flex gap-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: "var(--accent-text-soft)",
                  animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    if (location === "/forgot-password") {
      return <ForgotPassword />;
    }
    if (location.startsWith("/reset-password/")) {
      return (
        <Switch>
          <Route path="/reset-password/:token" component={ResetPassword} />
        </Switch>
      );
    }
    if (location !== "/auth") {
      return <Redirect to="/auth" />;
    }
    return <Auth />;
  }

  if (!user.onboardingComplete) {
    if (location !== "/onboarding") {
      return <Redirect to="/onboarding" />;
    }
    return <ProfileBuilder />;
  }

  if (!user.tutorialComplete) {
    return (
      <Tutorial
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        }}
      />
    );
  }

  if (location === "/auth" || location === "/onboarding") {
    return <Redirect to="/" />;
  }

  return <MainApp userTimeZone={user.timezone ?? null} />;
}

function App() {
  return (
    <ErrorBoundary fallbackMessage="The app ran into an issue. Try refreshing.">
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AppShell />
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
